import React from 'react';
import { motion } from 'framer-motion';

export default function TermsOfService() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <div style={styles.pageContainer}>
      <motion.div
        style={styles.contentWrapper}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.header style={styles.header} variants={itemVariants}>
          <div style={styles.logoMark}>OMNIO</div>
          <h1 style={styles.title}>Terms of Service</h1>
          <div style={styles.divider}></div>
          <p style={styles.subtitle}>EFFECTIVE DATE: JANUARY 1, 2026</p>
        </motion.header>

        <motion.div style={styles.intro} variants={itemVariants}>
          <p style={styles.introText}>Welcome to Omnio.</p>
          <p style={styles.introText}>
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of the <span style={styles.highlight}>Omnio</span> platform,
            including mobile applications, services, content, and features (collectively, the &quot;Service&quot;).
          </p>
          <p style={styles.introText}>By using Omnio, you agree to these Terms.</p>
          <p style={styles.introText}>If you do not agree, please do not use the Service.</p>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>01 / About Omnio</h3>
          <div style={styles.text}>
            Omnio is a digital lifestyle system that provides AI-powered insights related to fitness, nutrition, recovery,
            habits, and mental wellness. Omnio is not a medical provider. The Service does not provide medical advice,
            diagnosis, or treatment. Always consult a qualified healthcare professional before making decisions related to your health.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>02 / Eligibility</h3>
          <div style={styles.text}>
            You must be at least 16 years old to use Omnio. By using the Service, you confirm that: you are legally
            capable of entering into this agreement, and the information you provide is accurate and complete.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>03 / Health & Wellness Disclaimer</h3>
          <div style={styles.text}>
            Omnio provides informational and educational guidance only. You understand and agree that: workout plans may
            involve physical exertion; nutrition suggestions are general guidance; supplement recommendations are
            informational; mental wellness insights are not therapy or medical care. You assume full responsibility for
            your decisions and actions.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>04 / User Responsibilities</h3>
          <div style={styles.text}>
            You agree to: provide accurate information; use the Service responsibly; not misuse or attempt to reverse
            engineer the platform; not use the Service for unlawful purposes. You are responsible for maintaining the
            confidentiality of your account.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>05 / Subscriptions & Payments</h3>
          <div style={styles.text}>
            The entire Omnio app will require a paid subscription to access. Claudia (advanced mental wellness sessions)
            is a separate, additional charge on top of the main app subscription.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>06 / Free Trial</h3>
          <div style={styles.text}>
            Since Omnio is newly launching, the app is freely available for a limited period. This free access will
            soon convert to a paid subscription. The exact duration of the free period and conversion date will be
            disclosed within the app. Once the trial ends, a subscription will be required to continue using the Service.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>07 / Intellectual Property</h3>
          <div style={styles.text}>
            All content, software, branding, algorithms, and design elements within Omnio are owned by Omnio and
            protected under applicable intellectual property laws. You may not copy, distribute, or modify any part of
            the Service without written permission.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>08 / Data & Privacy</h3>
          <div style={styles.text}>
            Your use of the Service is also governed by our Privacy Policy. Omnio uses user-provided data to personalize
            recommendations and improve the adaptive system. We do not sell personal data.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>09 / Limitation of Liability</h3>
          <div style={styles.text}>
            To the maximum extent permitted by law, Omnio shall not be liable for: injuries related to workouts; health
            outcomes; emotional or psychological outcomes; indirect or incidental damages. Use of the Service is at your
            own risk.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>10 / Modifications</h3>
          <div style={styles.text}>
            We may update these Terms from time to time. Continued use of the Service after changes means you accept the
            updated Terms.
          </div>
        </motion.div>

        <motion.div style={styles.section} variants={itemVariants}>
          <h3 style={styles.sectionTitle}>11 / Termination</h3>
          <div style={styles.text}>
            We reserve the right to suspend or terminate access to the Service if these Terms are violated. You may stop
            using Omnio at any time.
          </div>
        </motion.div>

        <motion.footer style={styles.footer} variants={itemVariants}>
          <p>OMNIO</p>
          <p style={{ opacity: 0.5 }}>© 2026</p>
        </motion.footer>
      </motion.div>
    </div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: '#050505',
    color: '#F5F5F5',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    display: 'flex',
    justifyContent: 'center',
    padding: '80px 24px',
  },
  contentWrapper: {
    maxWidth: '700px',
    width: '100%',
  },
  header: {
    marginBottom: '80px',
  },
  logoMark: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '4px',
    color: '#444',
    marginBottom: '20px',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    letterSpacing: '-1.5px',
    margin: '0 0 20px 0',
    background: 'linear-gradient(to bottom right, #fff, #888)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  divider: {
    height: '1px',
    width: '60px',
    backgroundColor: '#333',
    marginBottom: '20px',
  },
  subtitle: {
    fontSize: '11px',
    letterSpacing: '2px',
    color: '#666',
    textTransform: 'uppercase',
  },
  intro: {
    marginBottom: '80px',
  },
  introText: {
    fontSize: '1.25rem',
    lineHeight: '1.6',
    color: '#999',
    marginBottom: '1rem',
    fontWeight: '400',
  },
  section: {
    marginBottom: '40px',
    padding: '20px',
    borderLeft: '1px solid #333',
  },
  sectionTitle: {
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#fff',
    marginBottom: '12px',
    fontWeight: '600',
  },
  text: {
    fontSize: '1rem',
    lineHeight: '1.7',
    color: '#888',
    margin: 0,
  },
  highlight: {
    color: '#fff',
    fontWeight: '500',
  },
  footer: {
    marginTop: '100px',
    borderTop: '1px solid #222',
    paddingTop: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    letterSpacing: '1px',
    color: '#444',
    textTransform: 'uppercase',
  },
};
